import { patientDisplayName } from 'utils/orders';
import React, { FC, Fragment, useContext, useState } from 'react';
import { InvoicingState, Order } from 'models/order';
import ConfirmDialog from 'components/shared/confirm-dialog/confirm-dialog.component';
import { useTranslation } from 'react-i18next';
import { ApolloError, useMutation } from '@apollo/client';
import { unlinkAttachedScannerCaseTemplate } from 'apollo/templates/scanner-case';
import { ScannerCase } from 'models/scanner-case';
import AttachedScannerCase from './attached-scanner-case.component';
import { OrderDetailsContext } from 'pages/order-details/order-details-context';
import InfoText from 'components/shared/info-text.component';
import { Box, Button } from '@mui/material';
import AttachScannerCaseDialog from 'components/scanner-cases/shared/attach-scanner-case-dialog/attach-scanner-case-dialog.component';
import { formatPatientId } from 'utils/format';

type AttachedScansSectionProps = {
    order: Order;
    versionCheck: boolean;
    isCustomer: boolean;
    canShowAttachScannerCaseButton: boolean;
};
const AttachedScansSection: FC<AttachedScansSectionProps> = ({
    order,
    versionCheck,
    isCustomer,
    canShowAttachScannerCaseButton,
}: AttachedScansSectionProps) => {
    const { t: translate } = useTranslation(['orders', 'three-shape']);
    const { refetch } = useContext(OrderDetailsContext);
    const [deleteScanCaseAttachmentConfirmDialogOpen, setDeleteScanCaseAttachmentConfirmDialogOpen] = useState(false);
    const [unlinkScannerCaseId, setUnlinkScannerCaseId] = useState<string>();
    const [dentistAddScanDialogOpen, setDentistAddScanDialogOpen] = useState(false);

    const modifyLocked = () => {
        return order.isProductionFinished || order.invoicingState !== InvoicingState.NOT_READY;
    };

    const [unlinkAttachedScannerCase] = useMutation(unlinkAttachedScannerCaseTemplate, {
        onCompleted: () => {
            setDeleteScanCaseAttachmentConfirmDialogOpen(false);
            refetch();
        },
        onError: (error: ApolloError) => {
            console.log(error, 'error');
        },
    });
    const handleOpenUnlinkDialog = (scanCaseId: string) => {
        setUnlinkScannerCaseId(scanCaseId);
        setDeleteScanCaseAttachmentConfirmDialogOpen(true);
    };

    const handleCloseDialog = () => {
        setDeleteScanCaseAttachmentConfirmDialogOpen(false);
    };

    const handleDeleteScanCaseAttachment = async () => {
        await unlinkAttachedScannerCase({
            variables: {
                orderId: order._id,
                scannerCaseId: unlinkScannerCaseId,
            },
        });
    };

    const handleAttachScannerCaseClick = () => {
        setDentistAddScanDialogOpen(true);
    };

    const closeScanCaseDialog = (scannerCase?: ScannerCase) => {
        if (scannerCase) {
            refetch();
        }
        setDentistAddScanDialogOpen(false);
    };

    return (
        <Fragment>
            {order.attachedScannerCaseFiles && order.attachedScannerCaseFiles.length > 0 ? (
                <>
                    {/* A referenced scanner case that no longer resolves comes back as null. */}
                    {order.attachedScannerCaseFiles
                        ?.filter(Boolean)
                        .map((scanCase: ScannerCase) => (
                            <AttachedScannerCase
                                key={scanCase._id}
                                scannerCase={scanCase}
                                onOpenUnlinkDialog={handleOpenUnlinkDialog}
                            />
                        ))}
                </>
            ) : (
                <InfoText text={translate('orders:NO_ATTACHED_SCANS')} />
            )}
            {canShowAttachScannerCaseButton && (
                <Box>
                    <Button
                        onClick={handleAttachScannerCaseClick}
                        data-test="files-images-section.attach-intraoral-scan-button"
                    >
                        {translate('orders:ATTACH_INTRAORAL_SCAN')}
                    </Button>
                </Box>
            )}
            <ConfirmDialog
                open={deleteScanCaseAttachmentConfirmDialogOpen}
                handleClose={handleCloseDialog}
                dialogContent={translate('three-shape:CONFIRM_DELETE_SCAN_ATTACHMENT')}
                handleConfirm={handleDeleteScanCaseAttachment}
            />
            <AttachScannerCaseDialog
                orderId={order._id}
                pin={formatPatientId(order.patient)}
                name={patientDisplayName(order.patient)}
                open={dentistAddScanDialogOpen}
                onClose={closeScanCaseDialog}
            />
        </Fragment>
    );
};

export default AttachedScansSection;
